import MedicineService from "../medicines/MedicineService";
import LabInvestigationService from "../labInvestigations/LabInvestigationService";
import { readingTag } from "../labInvestigations/data";
import ChiefComplaintsService from "../chiefComplaints/ChiefComplaintsService";

/**
 * Data-fetch helpers for the three "link entries" sections of the Diagnosis
 * Add / Update dialogs. Each fetch pulls the patient's full doc from the
 * respective v2 service, flattens active + past into one list, sorts by
 * latest-touched-first, and adapts to the shape the strip / picker expect:
 *
 *   { id, label, subLabel?, statusMark? }
 *
 * Latest-first sort key (fallback chain):
 *   1. `current.sinceWhen`
 *   2. `updatedAt`
 *   3. `addedAt`
 *
 * The statusMark surface is small so the chip strip can render a soft pill
 * next to the label — "resolved", "ended", "flagged" — so clinicians see at
 * a glance that a legacy entry is no longer current.
 */

const parseDate = (v) => {
  if (!v) return 0;
  const t = new Date(v).getTime();
  return isNaN(t) ? 0 : t;
};

const latestTouchedAt = (entry) => {
  const cur = (entry && entry.current) || {};
  return (
    parseDate(cur.sinceWhen) ||
    parseDate(entry && entry.updatedAt) ||
    parseDate(entry && entry.addedAt) ||
    parseDate(entry && entry.testDate)
  );
};

// Medicine v2 stores `medicine` + `formulation` as plain strings on the
// entry (not nested objects), plus a resolved `formulationName` populated
// server-side from the formulation catalog. Prefer the resolved human label
// so the chip reads "Atortest 20mg (Tablet)" rather than surfacing the raw
// REFLD code ("Atortest 20mg (REFLD169)"). Same fallback ladder MedicineName
// in Medicines.js uses — keeps the two surfaces consistent.
// Dose lives on the current-state snapshot as `{value, unit}`.
const medicineLabel = (m) => {
  const name = (m.medicine || "").trim() || "Medicine";
  const formLabel = ((m.formulationName || m.formulation) || "").trim();
  const dq = (m.current && m.current.doseQuantity) || {};
  const doseParts = [];
  if (dq.value != null && dq.value !== "") doseParts.push(String(dq.value));
  if (dq.unit) doseParts.push(dq.unit);
  const dose = doseParts.join(" ").trim();

  const nameWithForm = formLabel ? `${name} (${formLabel})` : name;
  return dose ? `${nameWithForm} · ${dose}` : nameWithForm;
};

// ─── Medicines ─────────────────────────────────────────────────────────

export const fetchMedicines = (patientId) =>
  new Promise((resolve) => {
    const svc = new MedicineService();
    svc.list(
      patientId,
      (res) => {
        const list = [
          ...((res && res.active) || []),
          ...((res && res.past) || []),
        ];
        const rows = list
          .filter((m) => m && m.medicineId)
          .map((m) => {
            const isEnded = m.status && m.status !== "ongoing";
            return {
              id: String(m.medicineId),
              label: medicineLabel(m),
              subLabel: (m.formulation || "").trim(),
              statusMark: isEnded ? "ended" : null,
              _sortAt: latestTouchedAt(m),
            };
          })
          .sort((a, b) => b._sortAt - a._sortAt);
        resolve(rows);
      },
      () => resolve([])
    );
  });

// ─── Chief complaints ──────────────────────────────────────────────────

export const fetchComplaints = (patientId) =>
  new Promise((resolve) => {
    const svc = new ChiefComplaintsService();
    svc.list(
      patientId,
      (res) => {
        const list = [
          ...((res && res.active) || []),
          ...((res && res.past) || []),
        ];
        const rows = list
          .filter((c) => c && c.complaintId)
          .map((c) => {
            const name = c.complaint || "Complaint";
            const icd =
              c.complaintCode && c.complaintCode.icdCode
                ? c.complaintCode.icdCode
                : "";
            // Primary chip label: name + ICD (when present). Description
            // moves to the picker's subLabel so the strip stays scannable.
            const label = icd ? `${name} · ICD ${icd}` : name;
            return {
              id: String(c.complaintId),
              label,
              subLabel: (c.current && c.current.description) || "",
              statusMark: c.ongoing ? null : "resolved",
              _sortAt: latestTouchedAt(c),
            };
          })
          .sort((a, b) => b._sortAt - a._sortAt);
        resolve(rows);
      },
      () => resolve([])
    );
  });

// ─── Labs ──────────────────────────────────────────────────────────────

// LI v2 returns a flat measurements list; each investigation carries its own
// investigationId + test metadata. Latest-first by testDate. No past/active
// split in LI v2.

// Latest per test — a test that's been sampled multiple times shouldn't
// show every historical row in the inline strip. Panel + test id + name are
// used as the identity key (matches LabInvestigations' groupByTest rule).
const testIdentity = (inv) =>
  (inv.test && inv.test.sltCode) ||
  (inv.test && inv.test.name) ||
  String(inv.investigationId || "");

export const fetchLabs = (patientId) =>
  new Promise((resolve) => {
    const svc = new LabInvestigationService();
    svc.list(
      patientId,
      (res) => {
        const list = (res && res.investigations) || [];
        // Collapse to latest-per-test so the strip / picker aren't dominated
        // by repeat samples of the same lab.
        const latestByTest = new Map();
        for (const inv of list) {
          if (!inv || !inv.investigationId) continue;
          const key = testIdentity(inv);
          const prev = latestByTest.get(key);
          if (!prev || parseDate(inv.testDate) > parseDate(prev.testDate)) {
            latestByTest.set(key, inv);
          }
        }
        const rows = Array.from(latestByTest.values())
          .map((inv) => {
            const name = (inv.test && inv.test.name) || "Investigation";
            const unit = (inv.test && inv.test.unit) || "";
            const val = inv.value != null ? String(inv.value) : "";
            // The value is the latest reading's, so its tag says which reading
            // it was. LAST, so the chips' ellipsis cuts the tag, not the value.
            const tag = readingTag(inv.test);
            const label = `${val ? `${name} (${val}${unit ? " " + unit : ""})` : name}${tag ? ` · ${tag}` : ""}`;
            const flag =
              inv.flag && inv.flag !== "normal" && inv.flag !== "unknown"
                ? inv.flag
                : null;
            return {
              id: String(inv.investigationId),
              label,
              subLabel: (inv.test && inv.test.panel) || "",
              statusMark: flag,
              _sortAt: parseDate(inv.testDate),
            };
          })
          .sort((a, b) => b._sortAt - a._sortAt);
        resolve(rows);
      },
      () => resolve([])
    );
  });
